import { ActionsUnion, createAction } from "../../../store/definitions";
import {
    CoachingActionTypes,
    ICoachingComment,
    ICoachingPendingCompletion,
    ICoachingPendingSessionResolution,
    ICoachingSession
} from "./types";

export interface ISetCoachingSessionsPayload {
    userId: string;
    sessions: ICoachingSession[];
}

export interface IUpdateCoachingSessionPayload {
    userId: string;
    session: ICoachingSession;
}

export interface ISetCoachingCommentsPayload {
    userId: string;
    sessionId: string;
    comments: ICoachingComment[];
}

export interface IAddPendingCoachingCommentPayload {
    userId: string;
    sessionId: string;
    comment: ICoachingComment;
}

/** Patch the sync layer applies to a single pending comment (resolve / fail / retry). */
export type ICoachingCommentPatch = Partial<
    Pick<ICoachingComment, "modified" | "_syncCounter" | "syncStatus" | "failureReason" | "dateTime">
>;

export interface IUpdatePendingCommentStatePayload {
    userId: string;
    sessionId: string;
    commentId: string;
    patch: ICoachingCommentPatch;
}

export interface IEnqueueCoachingCompletionPayload {
    userId: string;
    sessionId: string;
    completion: ICoachingPendingCompletion;
}

/**
 * Patch the sync layer applies to a single pending completion. Including `sessionId`
 * re-keys the record in the dictionary (used when a NEW session is resolved to a real id).
 */
export type ICoachingCompletionPatch = Partial<
    Pick<ICoachingPendingCompletion, "sessionId" | "modified" | "_syncCounter" | "syncStatus" | "failureReason">
>;

export interface IUpdateCoachingCompletionStatePayload {
    userId: string;
    sessionId: string;
    patch: ICoachingCompletionPatch;
}

export interface IReassignPendingCommentsPayload {
    userId: string;
    fromSessionId: string;
    toSessionId: string;
}

export interface IEnqueueNewSessionResolutionPayload {
    userId: string;
}

export interface ISetNewSessionResolutionPayload {
    userId: string;
    resolution?: ICoachingPendingSessionResolution;
}

export interface IRemovePendingSessionBucketPayload {
    userId: string;
    sessionId: string;
}

export const CoachingActions = {
    setCoachingSessions: (payload: ISetCoachingSessionsPayload) =>
        createAction(CoachingActionTypes.SET_COACHING_SESSIONS, payload),
    updateCoachingSession: (payload: IUpdateCoachingSessionPayload) =>
        createAction(CoachingActionTypes.UPDATE_COACHING_SESSION, payload),
    setCoachingComments: (payload: ISetCoachingCommentsPayload) =>
        createAction(CoachingActionTypes.SET_COACHING_COMMENTS, payload),
    addPendingCoachingComment: (payload: IAddPendingCoachingCommentPayload) =>
        createAction(CoachingActionTypes.ADD_PENDING_COACHING_COMMENT, payload),
    updatePendingCommentState: (payload: IUpdatePendingCommentStatePayload) =>
        createAction(CoachingActionTypes.UPDATE_PENDING_COMMENT_STATE, payload),
    enqueueCoachingCompletion: (payload: IEnqueueCoachingCompletionPayload) =>
        createAction(CoachingActionTypes.ENQUEUE_COACHING_COMPLETION, payload),
    updateCoachingCompletionState: (payload: IUpdateCoachingCompletionStatePayload) =>
        createAction(CoachingActionTypes.UPDATE_COACHING_COMPLETION_STATE, payload),
    clearCoachingState: (payload: string | undefined) =>
        createAction(CoachingActionTypes.CLEAR_COACHING_STATE, payload),
    reassignPendingComments: (payload: IReassignPendingCommentsPayload) =>
        createAction(CoachingActionTypes.REASSIGN_PENDING_COMMENTS, payload),
    enqueueNewSessionResolution: (payload: IEnqueueNewSessionResolutionPayload) =>
        createAction(CoachingActionTypes.ENQUEUE_NEW_SESSION_RESOLUTION, payload),
    setNewSessionResolution: (payload: ISetNewSessionResolutionPayload) =>
        createAction(CoachingActionTypes.SET_NEW_SESSION_RESOLUTION, payload),
    removePendingSessionComments: (payload: IRemovePendingSessionBucketPayload) =>
        createAction(CoachingActionTypes.REMOVE_PENDING_SESSION_COMMENTS, payload),
    removePendingSessionCompletion: (payload: IRemovePendingSessionBucketPayload) =>
        createAction(CoachingActionTypes.REMOVE_PENDING_SESSION_COMPLETION, payload)
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CoachingActions = ActionsUnion<typeof CoachingActions>;
